import type { EnrollmentUserProfile } from '@/api/enrollment-api'
import { readWidgetLeadData } from '../state/widgetLeadStorage'
import {
  getEnrollmentDraftAddress,
  getEnrollmentDraftAddressLine1,
  getEnrollmentDraftBirthDate,
  getEnrollmentDraftCity,
  getEnrollmentDraftEmail,
  getEnrollmentDraftFirstName,
  getEnrollmentDraftLastName,
  getEnrollmentDraftMiddleName,
  getEnrollmentDraftPhoneNumber,
  getEnrollmentDraftState,
  getEnrollmentDraftZipCode
} from './enrollmentDraftSelectors'

export type EnrollmentBootstrapFields = {
  firstName: string
  lastName: string
  middleName: string
  email: string
  phoneNumber: string
  address: string
  addressLine1: string
  city: string
  state: string
  zipCode: string
  birthDate: string
  about: string
  referredBy: string
}

export type EnrollmentProfileBootstrapFields = EnrollmentBootstrapFields & {
  smsConsent?: boolean
}

const emptyBootstrapFields: EnrollmentBootstrapFields = {
  firstName: '',
  lastName: '',
  middleName: '',
  email: '',
  phoneNumber: '',
  address: '',
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
  birthDate: '',
  about: '',
  referredBy: ''
}

export const getEmptyBootstrapFields = (): EnrollmentBootstrapFields => ({
  ...emptyBootstrapFields
})

export const readEnrollmentDraftBootstrapFields = (): EnrollmentBootstrapFields => ({
  firstName: getEnrollmentDraftFirstName(),
  lastName: getEnrollmentDraftLastName(),
  middleName: getEnrollmentDraftMiddleName(),
  email: getEnrollmentDraftEmail(),
  phoneNumber: getEnrollmentDraftPhoneNumber(),
  address: getEnrollmentDraftAddress(),
  addressLine1: getEnrollmentDraftAddressLine1(),
  city: getEnrollmentDraftCity(),
  state: getEnrollmentDraftState(),
  zipCode: getEnrollmentDraftZipCode(),
  birthDate: getEnrollmentDraftBirthDate(),
  about: '',
  referredBy: ''
})

export const readWidgetLeadBootstrapFields = (): EnrollmentBootstrapFields => {
  const widgetLead = readWidgetLeadData()

  return {
    firstName: widgetLead.firstName ?? '',
    lastName: widgetLead.lastName ?? '',
    middleName: widgetLead.middleName ?? '',
    email: widgetLead.email ?? '',
    phoneNumber: widgetLead.phone ?? '',
    address: widgetLead.address ?? '',
    addressLine1: widgetLead.addressLine1 ?? '',
    city: widgetLead.city ?? '',
    state: widgetLead.state ?? '',
    zipCode: widgetLead.zipCode ?? '',
    birthDate: '',
    about: widgetLead.about ?? '',
    referredBy: widgetLead.referredBy ?? ''
  }
}

export const getEnrollmentProfileBootstrapFields = (
  profile: EnrollmentUserProfile | null | undefined
): EnrollmentProfileBootstrapFields => {
  const personalData = profile?.personalData

  return {
    firstName: profile?.firstName?.trim() || '',
    lastName: profile?.lastName?.trim() || '',
    middleName: profile?.middleName?.trim?.() || '',
    phoneNumber: personalData?.phoneNumber?.trim() || '',
    email: profile?.email?.trim() || '',
    address: '',
    addressLine1: personalData?.addressLine1?.trim() || '',
    city: personalData?.city?.trim() || '',
    state: personalData?.state?.trim() || '',
    zipCode: personalData?.zipCode?.trim() || '',
    birthDate: personalData?.dateOfBirth || '',
    about: '',
    referredBy: '',
    smsConsent: typeof personalData?.smsConsent === 'boolean' ? personalData.smsConsent : undefined
  }
}

export const shouldUseEnrollmentDraftBootstrap = ({
  draftEmail,
  normalizeEmail,
  widgetLeadEmail
}: {
  draftEmail?: string | null
  normalizeEmail: (value?: string | null) => string
  widgetLeadEmail?: string | null
}) => {
  const normalizedDraftEmail = normalizeEmail(draftEmail)
  const normalizedWidgetEmail = normalizeEmail(widgetLeadEmail)

  return (
    !normalizedWidgetEmail ||
    !normalizedDraftEmail ||
    normalizedWidgetEmail === normalizedDraftEmail
  )
}
